/**
 * IconPickerGrid.jsx
 *
 * Reusable visual icon picker that renders a searchable grid of React Icon
 * components. Matches the look-and-feel of the Board Creation Rules icon
 * selector so every settings page is consistent.
 *
 * Usage:
 *   <IconPickerGrid
 *       value={formData.icon}
 *       onChange={(iconValue) => setFormData(f => ({ ...f, icon: iconValue }))}
 *       options={ICON_OPTIONS}            // optional – uses built-in set when omitted
 *   />
 *
 * Each option is { value: string, label: string, Icon: ReactComponent }.
 */

import React, { useState } from 'react';
import { IoSearch, IoClose } from 'react-icons/io5';
import {
    FaTicketAlt, FaLaptop, FaClipboardList, FaTools, FaExclamationTriangle,
    FaBug, FaFileAlt, FaCog, FaShieldAlt, FaUserCog, FaHeadset, FaDesktop,
    FaServer, FaNetworkWired, FaDatabase, FaChartBar, FaClipboardCheck,
    FaTasks, FaWrench, FaKey, FaEnvelope, FaBell, FaCalendarAlt,
    FaBuilding, FaUsers, FaUserTie, FaHandshake, FaMoneyBillWave,
    FaChartLine, FaLeaf, FaRecycle, FaFire, FaLock, FaGlobe,
    FaPhone, FaCamera, FaPlug, FaWifi, FaPrint, FaHdd, FaTruck,
    FaBoxOpen, FaLightbulb, FaHammer, FaPaintBrush, FaHome,
    FaMobile, FaCloud, FaUserShield, FaBook, FaProjectDiagram,
    FaShoppingCart, FaFolder, FaTag, FaCode
} from 'react-icons/fa';

/**
 * Default curated icon set used when no `options` prop is supplied.
 * Covers IT, operations, business and general categories.
 */
const DEFAULT_OPTIONS = [
    { value: 'fa-ticket-alt', label: 'Ticket', Icon: FaTicketAlt },
    { value: 'fa-laptop', label: 'Laptop', Icon: FaLaptop },
    { value: 'fa-desktop', label: 'Desktop', Icon: FaDesktop },
    { value: 'fa-server', label: 'Server', Icon: FaServer },
    { value: 'fa-print', label: 'Printer', Icon: FaPrint },
    { value: 'fa-mobile', label: 'Mobile', Icon: FaMobile },
    { value: 'fa-hdd', label: 'Storage', Icon: FaHdd },
    { value: 'fa-wifi', label: 'WiFi', Icon: FaWifi },
    { value: 'fa-network-wired', label: 'Network', Icon: FaNetworkWired },
    { value: 'fa-cloud', label: 'Cloud', Icon: FaCloud },
    { value: 'fa-database', label: 'Database', Icon: FaDatabase },
    { value: 'fa-clipboard-list', label: 'Checklist', Icon: FaClipboardList },
    { value: 'fa-clipboard-check', label: 'Audit', Icon: FaClipboardCheck },
    { value: 'fa-tools', label: 'Tools', Icon: FaTools },
    { value: 'fa-wrench', label: 'Maintenance', Icon: FaWrench },
    { value: 'fa-hammer', label: 'Hammer', Icon: FaHammer },
    { value: 'fa-cog', label: 'Settings', Icon: FaCog },
    { value: 'fa-bug', label: 'Bug', Icon: FaBug },
    { value: 'fa-code', label: 'Code', Icon: FaCode },
    { value: 'fa-exclamation-triangle', label: 'Warning', Icon: FaExclamationTriangle },
    { value: 'fa-shield-alt', label: 'Security', Icon: FaShieldAlt },
    { value: 'fa-user-shield', label: 'User Security', Icon: FaUserShield },
    { value: 'fa-user-cog', label: 'User Config', Icon: FaUserCog },
    { value: 'fa-key', label: 'Access', Icon: FaKey },
    { value: 'fa-lock', label: 'Lock', Icon: FaLock },
    { value: 'fa-headset', label: 'Support', Icon: FaHeadset },
    { value: 'fa-phone', label: 'Phone', Icon: FaPhone },
    { value: 'fa-envelope', label: 'Email', Icon: FaEnvelope },
    { value: 'fa-bell', label: 'Alert', Icon: FaBell },
    { value: 'fa-file-alt', label: 'Document', Icon: FaFileAlt },
    { value: 'fa-book', label: 'Knowledge', Icon: FaBook },
    { value: 'fa-folder', label: 'Folder', Icon: FaFolder },
    { value: 'fa-tag', label: 'Tag', Icon: FaTag },
    { value: 'fa-tasks', label: 'Tasks', Icon: FaTasks },
    { value: 'fa-calendar-alt', label: 'Calendar', Icon: FaCalendarAlt },
    { value: 'fa-building', label: 'Building', Icon: FaBuilding },
    { value: 'fa-users', label: 'Team', Icon: FaUsers },
    { value: 'fa-user-tie', label: 'Manager', Icon: FaUserTie },
    { value: 'fa-handshake', label: 'Agreement', Icon: FaHandshake },
    { value: 'fa-money-bill-wave', label: 'Finance', Icon: FaMoneyBillWave },
    { value: 'fa-chart-bar', label: 'Report', Icon: FaChartBar },
    { value: 'fa-chart-line', label: 'Analytics', Icon: FaChartLine },
    { value: 'fa-project-diagram', label: 'Project', Icon: FaProjectDiagram },
    { value: 'fa-shopping-cart', label: 'Procurement', Icon: FaShoppingCart },
    { value: 'fa-truck', label: 'Delivery', Icon: FaTruck },
    { value: 'fa-box-open', label: 'Package', Icon: FaBoxOpen },
    { value: 'fa-globe', label: 'Global', Icon: FaGlobe },
    { value: 'fa-camera', label: 'Camera', Icon: FaCamera },
    { value: 'fa-plug', label: 'Plug', Icon: FaPlug },
    { value: 'fa-leaf', label: 'Sustainability', Icon: FaLeaf },
    { value: 'fa-recycle', label: 'Recycle', Icon: FaRecycle },
    { value: 'fa-fire', label: 'Urgent', Icon: FaFire },
    { value: 'fa-lightbulb', label: 'Idea', Icon: FaLightbulb },
    { value: 'fa-paint-brush', label: 'Design', Icon: FaPaintBrush },
    { value: 'fa-home', label: 'Home', Icon: FaHome },
];

const styles = {
    wrapper: { marginTop: '4px' },
    selectedPreview: {
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe',
        borderRadius: '8px', fontSize: '0.85rem', color: '#1e40af', marginBottom: '8px'
    },
    clearBtn: {
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#6b7280', padding: '2px', display: 'inline-flex', alignItems: 'center'
    },
    searchWrapper: { position: 'relative', marginTop: '6px' },
    searchInput: {
        width: '100%', padding: '6px 10px 6px 30px',
        border: '1px solid #d1d5db', borderRadius: '6px',
        fontSize: '0.85rem', boxSizing: 'border-box'
    },
    searchIcon: {
        position: 'absolute', left: '8px', top: '50%',
        transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none'
    },
    grid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
        gap: '6px', maxHeight: '200px', overflowY: 'auto',
        border: '1px solid #e5e7eb', borderRadius: '8px',
        padding: '8px', marginTop: '6px'
    },
    cell: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '8px 4px', borderRadius: '8px',
        cursor: 'pointer', border: '2px solid transparent',
        transition: 'all 0.15s', gap: '4px', minHeight: '56px'
    },
    cellSelected: { border: '2px solid #2563eb', background: '#eff6ff' },
    label: { fontSize: '0.65rem', color: '#6b7280', textAlign: 'center', lineHeight: 1.2 },
};

/**
 * IconPickerGrid — visual grid icon selector matching the Board Creation Rules style.
 *
 * @param {string}   value       Currently selected icon value (fa-class or React Icon name)
 * @param {function} onChange    Callback with the new value (or '' to clear)
 * @param {Array}    [options]   Optional custom icon option list
 */
const IconPickerGrid = ({ value, onChange, options }) => {
    const [search, setSearch] = useState('');
    const iconOptions = options || DEFAULT_OPTIONS;

    const selected = iconOptions.find(o => o.value === value);

    return (
        <div style={styles.wrapper}>
            {/* Current selection preview */}
            {selected && (
                <div style={styles.selectedPreview}>
                    <selected.Icon size={16} />
                    <span>{selected.label}</span>
                    <button type="button" style={styles.clearBtn} onClick={() => onChange('')} title="Clear icon">
                        <IoClose size={14} />
                    </button>
                </div>
            )}

            {/* Search filter */}
            <div style={styles.searchWrapper}>
                <IoSearch size={14} style={styles.searchIcon} />
                <input
                    style={styles.searchInput}
                    placeholder="Search icons..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {/* Icon grid */}
            <div style={styles.grid}>
                {iconOptions
                    .filter(opt => !search || opt.label.toLowerCase().includes(search.toLowerCase()))
                    .map(opt => {
                        const isSelected = value === opt.value;
                        return (
                            <div
                                key={opt.value}
                                style={{ ...styles.cell, ...(isSelected ? styles.cellSelected : {}) }}
                                onClick={() => onChange(isSelected ? '' : opt.value)}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f3f4f6'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ''; }}
                                title={opt.label}
                            >
                                <opt.Icon size={20} color={isSelected ? '#2563eb' : '#6b7280'} />
                                <span style={styles.label}>{opt.label}</span>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export { DEFAULT_OPTIONS };
export default IconPickerGrid;
